import { Color } from '@/GlobalStyles';
import HelpButton from '@/components/HelpButton';
import { FormChildProp } from '@/components/Form/types';
import Slider from '@react-native-community/slider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text, useTheme } from 'react-native-paper';

type DurationSliderInputProps = FormChildProp<Date | undefined> & {
  defaultValue?: Date;
  value?: Date;
  minimumMinutes?: number;
  maximumMinutes?: number;
  stepMinutes?: number;
  withMargin?: boolean;
  withHelpButton?: boolean;
  helpFieldName?: string;
};

const minutesFromDate = (d?: Date): number => {
  if (!d) return 0;
  return d.getHours() * 60 + d.getMinutes();
};

const dateFromMinutes = (minutes: number): Date => {
  const d = new Date();
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
};

const formatDuration = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) {
    return `${m} min`;
  }
  if (m === 0) {
    return `${h} h`;
  }
  return `${h} h ${m.toString().padStart(2, '0')}`;
};

const DurationSliderInput = React.forwardRef(
  (props: DurationSliderInputProps, _ref): React.ReactElement => {
    const {
      defaultValue,
      value,
      onChangeValue,
      containerStyle,
      label,
      labelStyle,
      withMargin = true,
      withHelpButton = false,
      helpFieldName,
      name,
      minimumMinutes = 0,
      maximumMinutes = 240,
      stepMinutes = 15,
    } = props;

    const theme = useTheme();
    const { t } = useTranslation();

    const initial = value !== undefined ? value : defaultValue;
    const [currentMinutes, setCurrentMinutes] = useState<number>(minutesFromDate(initial));

    useEffect(() => {
      const next = value !== undefined ? value : defaultValue;
      const nextMin = minutesFromDate(next);
      if (nextMin !== currentMinutes) {
        setCurrentMinutes(nextMin);
      }
    }, [defaultValue, value]);

    const emit = (minutes: number) => {
      setCurrentMinutes(minutes);
      if (onChangeValue) {
        onChangeValue(dateFromMinutes(minutes));
      }
    };

    return (
      <View
        style={[
          styles.container,
          withMargin ? { marginBottom: 10 } : null,
          containerStyle,
        ]}
      >
        {label ? (
          <View style={styles.labelContainer}>
            <Text variant={'labelMedium'} style={[styles.label, labelStyle]} numberOfLines={2}>
              {t(label)}
            </Text>
            {withHelpButton && (helpFieldName || name) ? (
              <HelpButton
                fieldName={helpFieldName || name || ''}
                size={16}
                style={{ marginLeft: 4 }}
              />
            ) : null}
          </View>
        ) : null}
        <View style={styles.sliderRow}>
          <Slider
            style={styles.slider}
            minimumValue={minimumMinutes}
            maximumValue={maximumMinutes}
            step={stepMinutes}
            value={currentMinutes}
            onValueChange={emit}
            minimumTrackTintColor={theme.colors.primary}
            maximumTrackTintColor={Color.colorGray_100}
            thumbTintColor={theme.colors.primary}
          />
          <Text variant={'labelLarge'} style={styles.valueText}>
            {formatDuration(currentMinutes)}
          </Text>
        </View>
      </View>
    );
  }
);

DurationSliderInput.displayName = 'DurationSliderInput';

export default DurationSliderInput;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  label: {
    flexShrink: 1,
  },
  sliderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 8,
  },
  slider: {
    flex: 1,
    height: 40,
  },
  valueText: {
    minWidth: 64,
    textAlign: 'right',
  },
});
